"use client";

import {
  IconAlertWarningOutline18,
  IconArrowTrendDownOutline18,
  IconArrowTrendUpOutline18,
} from "nucleo-ui-outline-18";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Skeleton loading rows
export function LoadingSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="animate-in fade-in duration-300">
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-16 animate-pulse rounded-lg" />
        ))}
      </div>
    </div>
  );
}

// Error state with retry
export function ErrorState({ title, message, onRetry }: { title: string; message?: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20">
        <IconAlertWarningOutline18 className="h-8 w-8 text-red-600 dark:text-red-500" />
      </div>
      <h3 className="text-ds-text-primary mb-2 text-lg font-semibold">{title}</h3>
      {message && <p className="body-sm mb-6">{message}</p>}
      <Button onClick={onRetry}>
        <span className="text-xs">Try again</span>
      </Button>
    </div>
  );
}

// Unified PnL formatter.
// - Percentage-only:       formatPnl(pnlPct)           → "+1.23%" / "-4.56%" / "0.00%"
// - Cash + percentage:     formatPnl(percentPnl, cash)  → "+$1.00 (+2.50%)" / "-$1.00 (-2.50%)" / "$0.00 (0.00%)"
export function formatPnl(
  percentPnl: number,
  cashPnl?: number,
): { text: string; className: string; icon: React.ReactNode } {
  const hasCash = cashPnl !== undefined;

  // Determine direction from cashPnl when provided, otherwise from percentPnl
  const direction = hasCash ? Math.sign(cashPnl) : Math.sign(percentPnl);

  if (direction > 0) {
    const pctText = `+${Math.abs(percentPnl).toFixed(2)}%`;
    const text = hasCash ? `+$${Math.abs(cashPnl).toFixed(2)} (${pctText})` : pctText;
    return {
      text,
      className: "text-green-600 dark:text-green-400",
      icon: <IconArrowTrendUpOutline18 className="h-3.5 w-3.5" />,
    };
  }

  if (direction < 0) {
    const pctText = `-${Math.abs(percentPnl).toFixed(2)}%`;
    const text = hasCash ? `-$${Math.abs(cashPnl).toFixed(2)} (${pctText})` : pctText;
    return {
      text,
      className: "text-red-600 dark:text-red-400",
      icon: <IconArrowTrendDownOutline18 className="h-3.5 w-3.5" />,
    };
  }

  return {
    text: hasCash ? "$0.00 (0.00%)" : "0.00%",
    className: "text-ds-text-muted",
    icon: null,
  };
}

// Empty state with customizable icon
export function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
        <Icon className="text-ds-text-disabled dark:text-ds-text-subtle h-8 w-8" />
      </div>
      <h3 className="text-ds-text-primary mb-2 text-lg font-semibold">{title}</h3>
      <p className="body-sm mb-6">{description}</p>
    </div>
  );
}

export function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "label-xs relative px-3 pb-2.5 pt-2 transition-colors",
        active ? "text-ds-text-primary" : "text-ds-text-muted hover:text-ds-text-primary",
      )}
    >
      {children}
      {active && <span className="bg-ds-focus-brand absolute inset-x-0 bottom-0 h-0.5 rounded-full" />}
    </button>
  );
}

export function DirectionBadge({ label, positive }: { label: string; positive: boolean }) {
  return <Badge variant={positive ? "success" : "destructive"}>{label}</Badge>;
}

export function CompactEmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="border-ds-border-default text-ds-text-muted bg-ds-surface-muted rounded-xl border border-dashed px-4 py-5 text-sm">
      <div className="text-ds-text-secondary font-medium">{title}</div>
      <p className="text-ds-text-muted mt-1 text-sm">{description}</p>
    </div>
  );
}

export function NestedBranchCard({
  children,
  isFirst,
  isLast,
  className,
  variant = "card",
}: {
  children: React.ReactNode;
  isFirst: boolean;
  isLast: boolean;
  className?: string;
  variant?: "card" | "divider";
}) {
  const isDivider = variant === "divider";

  return (
    <div className={cn("relative pl-12 md:pl-14", isDivider ? "py-0" : "py-3")}>
      {!isFirst && (
        <div
          className={cn(
            "border-ds-neutral-300 dark:border-ds-neutral-700 absolute left-4 w-0 border-l-2 border-dashed",
            isDivider ? "bottom-1/2 top-0" : "-top-3 bottom-1/2",
          )}
        />
      )}
      <div className="rounded-bl-ds-sm border-ds-neutral-300 dark:border-ds-neutral-700 absolute left-4 top-1/2 z-[1] h-3 w-5 -translate-y-full border-b-2 border-l-2" />
      {!isLast && (
        <div
          className={cn(
            "border-ds-neutral-300 dark:border-ds-neutral-700 absolute left-4 top-1/2 w-0 border-l-2 border-dashed",
            isDivider ? "bottom-0" : "-bottom-3",
          )}
        />
      )}

      {isDivider ? (
        <div
          className={cn(
            "py-5 transition-colors duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)]",
            !isLast && "border-ds-neutral-200/80 dark:border-ds-neutral-800/80 border-b",
            className,
          )}
        >
          {children}
        </div>
      ) : (
        <div
          className={cn(
            "border-ds-border-default hover:border-ds-neutral-300 dark:hover:border-ds-neutral-700 bg-ds-surface-card rounded-xl border p-4 transition-[border-color,box-shadow,transform] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] hover:shadow-[0_12px_30px_-18px_rgba(15,23,42,0.2)] active:scale-[0.998]",
            className,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
